// ============================================================================
// STATISTIQUES › VENTES › REPORTING VENTES — un tableau, les niveaux que l'on choisit, les indicateurs de vente, de
// marge et de stock, et la ligne de total toujours sous les yeux. Réf. maquette du back-office (« Statistiques · Reporting
// ventes ») et docs/CADRAGE-Statistiques-Ventes.md §7.
//
// ⚠️ L'ÉCRAN TRIE ET DESSINE, IL NE DÉFINIT AUCUN NOMBRE : sommes, taux, parts et écarts viennent du serveur
//    (`sales/stats-report.ts`). Le squelette du tableau est celui de tous les reportings (`stats-grid.tsx`).
// ⚠️ UN INDICATEUR NON MESURABLE S'AFFICHE « — », JAMAIS ZÉRO.
// ⚠️ LES COLONNES DONT LA SOURCE N'EXISTE PAS ENCORE NE SONT PAS OFFERTES (objectif, atteinte) : une colonne de « — »
//    partout ferait croire à une mesure qui n'a rien trouvé.
// ⚠️ LE BLOC STOCK EST CELUI DU REPORTING STOCK (même moteur au serveur, stock de FIN de période — décision utilisateur) et
//    n'est demandé que si l'une de ses colonnes est affichée : le rejeu du grand livre est la lecture la plus lourde. De même
//    le bloc Commandes : les commandes passées dans la période, et ce qu'elles attendent encore aujourd'hui.
// ============================================================================
import { Trans } from '@orgazone/ui-web';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import type {
  SalesOrderMetric, SalesOrderValues, SalesReportLevel, SalesReportMetric, SalesReportValues, SalesReportView, SalesStockMetric, SalesStockValues,
} from '../api';
import { key, t, type Translatable } from '../i18n';
import { fieldLabel } from '../vocabulary';
import type { VocabularyFieldKey } from '@orgazone/sync-contract';
import { exporterCsv } from './export-csv';
import { FILTER_DIMS, dimLabel } from './stats-bar';
import { coef, csvCoef, csvQty, csvRate, dTx, eur0, pc, qty, shownValue } from './stats-format';
import { StatsColumnsButton, StatsGrid, sortStatsRows, useStatsColumns, type GridCell, type StatsSort } from './stats-grid';

// ─── Les colonnes ────────────────────────────────────────────────────────────────────────────────

/** Ce que l'on lit dans chaque cellule : la période, la comparaison, ou l'écart entre les deux. */
export type ReportDisplay = 'a' | 'b' | 'd';

/** Le tri : une colonne (`ax:<rang du niveau>` pour un niveau), son sens, et s'il a été choisi. */
export type ReportSort = StatsSort;
export const REPORT_DEFAULT_SORT: ReportSort = { k: 'netTtcCents', d: -1, man: false };

type Block = 'sales' | 'margin' | 'stock' | 'orders' | 'share';
const BLOCKS: Record<Block, { label: Translatable; color: string }> = {
  sales: { label: key('Ventes'), color: 'var(--teal-500)' },
  margin: { label: key('Marge'), color: 'var(--violet)' },
  stock: { label: key('Stock'), color: 'var(--blue)' },
  orders: { label: key('Commandes'), color: 'var(--amber)' },
  share: { label: key('Répartition'), color: 'var(--ink-500)' },
};

/**
 * Les colonnes que le grand livre porte — lues dans `stock` de chaque ligne, et non dans ses ventes : le bloc Stock, et
 * les Rétrocessions (rangées avec les ventes comme dans le dessin, mais comptées au grand livre).
 */
const STOCK_METRICS: readonly SalesStockMetric[] = [
  'entriesQty', 'entriesValueCents', 'exitsQty', 'exitsValueCents', 'stockQty', 'shareStockBp', 'stockValueCents', 'sellThroughBp', 'retroQty',
];
/** Les colonnes du bloc Commandes — lues dans `orders` de chaque ligne. */
const ORDER_METRICS: readonly SalesOrderMetric[] = ['orderedValueCents', 'remainingValueCents', 'orderedQty', 'remainingQty'];
type ReportMetric = SalesReportMetric | SalesStockMetric | SalesOrderMetric;
const isStockMetric = (k: string): k is SalesStockMetric => (STOCK_METRICS as readonly string[]).includes(k);
const isOrderMetric = (k: string): k is SalesOrderMetric => (ORDER_METRICS as readonly string[]).includes(k);

type Unit = 'money' | 'rate' | 'qty' | 'coef';
interface ReportColumn {
  k: ReportMetric;
  label: Translatable;
  /**
   * Le champ de la fiche dont la colonne porte le nom — dans le MOT DU MAGASIN (`fieldLabel`), jamais dans celui du
   * dessin : un caviste qui appelle autrement son prix d'achat le lit ici aussi.
   */
  field?: VocabularyFieldKey;
  unit: Unit;
  /** La largeur minimale de la maquette ; la colonne s'élargit si son libellé ne tient pas. */
  width: number;
  block: Block;
}

/** Les colonnes, dans l'ordre du catalogue de la maquette — celles dont la source existe. */
export const REPORT_COLUMNS: ReportColumn[] = [
  { k: 'grossTtcCents', label: key('CA brut TTC'), unit: 'money', width: 104, block: 'sales' },
  { k: 'netTtcCents', label: key('CA net TTC'), unit: 'money', width: 104, block: 'sales' },
  { k: 'shareNetBp', label: key('Répartition CA net'), unit: 'rate', width: 108, block: 'share' },
  { k: 'netHtCents', label: key('CA net HT'), unit: 'money', width: 104, block: 'sales' },
  { k: 'discountRateBp', label: key('Taux remise'), unit: 'rate', width: 92, block: 'sales' },
  { k: 'qtySold', label: key('Qté vendue'), unit: 'qty', width: 82, block: 'sales' },
  { k: 'shareQtyBp', label: key('Répartition Qté'), unit: 'rate', width: 104, block: 'share' },
  { k: 'retroQty', label: key('Rétrocessions'), unit: 'qty', width: 96, block: 'sales' },
  { k: 'marginHtCents', label: key('Marge {symbole} HT'), unit: 'money', width: 104, block: 'margin' },
  { k: 'shareMarginBp', label: key('Répartition marge'), unit: 'rate', width: 108, block: 'share' },
  { k: 'marginRateBp', label: key('Marge %'), unit: 'rate', width: 84, block: 'margin' },
  { k: 'coefBp', label: key('Coef'), unit: 'coef', width: 74, block: 'margin' },
  { k: 'purchaseHtCents', label: key('{champ} HT'), field: 'purchasePriceExclTax', unit: 'money', width: 98, block: 'margin' },
  { k: 'entriesQty', label: key('Entrées'), unit: 'qty', width: 82, block: 'stock' },
  { k: 'entriesValueCents', label: key('Entrées {symbole} HT'), unit: 'money', width: 104, block: 'stock' },
  { k: 'exitsQty', label: key('Sorties'), unit: 'qty', width: 82, block: 'stock' },
  { k: 'exitsValueCents', label: key('Sorties {symbole} HT'), unit: 'money', width: 104, block: 'stock' },
  { k: 'stockQty', label: key('Stock'), unit: 'qty', width: 80, block: 'stock' },
  { k: 'shareStockBp', label: key('Répartition stock'), unit: 'rate', width: 108, block: 'share' },
  { k: 'stockValueCents', label: key('Stock {symbole} HT'), unit: 'money', width: 104, block: 'stock' },
  { k: 'sellThroughBp', label: key('Taux sortie'), unit: 'rate', width: 92, block: 'stock' },
  { k: 'orderedValueCents', label: key('Cmd {symbole} HT'), unit: 'money', width: 100, block: 'orders' },
  { k: 'remainingValueCents', label: key('Reste à livrer {symbole} HT'), unit: 'money', width: 132, block: 'orders' },
  { k: 'orderedQty', label: key('Cmd Qté'), unit: 'qty', width: 84, block: 'orders' },
  { k: 'remainingQty', label: key('Reste Qté'), unit: 'qty', width: 88, block: 'orders' },
];

/** L'écran demande-t-il le bloc Stock ? Oui dès qu'une de ses colonnes est affichée. */
export const needsStock = (visible: readonly ReportColumn[]): boolean => visible.some((c) => isStockMetric(c.k));
/** L'écran demande-t-il le bloc Commandes ? Oui dès qu'une de ses colonnes est affichée. */
export const needsOrders = (visible: readonly ReportColumn[]): boolean => visible.some((c) => isOrderMetric(c.k));

/** La colonne qui ne se masque jamais. */
export const REPORT_KEY_COLUMN: SalesReportMetric = 'netTtcCents';

/** Le libellé d'une colonne — la devise et le nom du champ sont ceux du magasin. */
const columnLabel = (c: ReportColumn): string =>
  t(c.label, { symbole: currentCurrency().symbol, ...(c.field ? { champ: fieldLabel(c.field) } : {}) });

/** Les colonnes choisies de ce tableau, mémorisées sur le profil. */
export function useReportColumns(enabled: boolean, onSaveFailed: (message: string) => void) {
  return useStatsColumns('stats-ventes-reporting', REPORT_COLUMNS, REPORT_KEY_COLUMN, columnLabel, enabled, onSaveFailed);
}

/**
 * LES COLONNES DU REPORTING VENDEURS — celles du Reporting ventes, sans ce que le grand livre et les achats portent
 * (stock, rétrocessions, commandes) : ces faits n'ont pas de vendeur.
 */
export const SELLER_REPORT_COLUMNS: readonly ReportMetric[] = REPORT_COLUMNS.filter((c) => !isStockMetric(c.k) && !isOrderMetric(c.k)).map((c) => c.k);

/**
 * Les colonnes du Reporting vendeurs. ⚠️ LE RÉGLAGE EST CELUI DU REPORTING VENTES (maquette) : masquer une colonne ici la
 * masque là-bas, et une colonne absente d'ici y garde sa place.
 */
export function useSellerReportColumns(enabled: boolean, onSaveFailed: (message: string) => void) {
  return useStatsColumns('stats-ventes-reporting', REPORT_COLUMNS, REPORT_KEY_COLUMN, columnLabel, enabled, onSaveFailed, SELLER_REPORT_COLUMNS);
}

/** Le nom d'un niveau affiché : le vendeur, ou le mot du magasin pour un critère. */
const levelLabel = (k: SalesReportLevel): string => (k === 'seller' ? t('Vendeur') : dimLabel(FILTER_DIMS.find((d) => d.k === k)!));

/**
 * Ce qu'une ligne montre de ses niveaux. ⚠️ UN VENDEUR SE LIT PAR SON NOM : sa valeur est l'identifiant de l'opérateur, qui
 * regroupe (un nom corrigé ne fait pas deux vendeurs) mais ne dit rien à qui lit.
 */
const shownParts = (parts: readonly { value: string; label: string }[], axes: readonly SalesReportLevel[]) =>
  parts.map((p, i) => (axes[i] === 'seller' ? { value: p.label, label: p.label } : p));

/** Le bouton « Colonnes » de la barre. */
export const ReportColumnsButton = StatsColumnsButton;

/** La légende des blocs dont au moins une colonne est affichée. */
export const reportLegend = (visible: ReportColumn[]): { label: string; color: string }[] =>
  (Object.keys(BLOCKS) as Block[]).filter((b) => visible.some((c) => c.block === b))
    .map((b) => ({ label: t(BLOCKS[b].label), color: BLOCKS[b].color }));

// ─── L'affichage : période, comparaison, écart ───────────────────────────────────────────────────

/** « Afficher : Période · Comparaison · Écart » — au bout de la ligne PÉRIODE, quand on compare. */
export function ReportDisplayToggle({ value, onChange }: { value: ReportDisplay; onChange: (v: ReportDisplay) => void }) {
  const items: { v: ReportDisplay; l: string }[] = [
    { v: 'a', l: t('Période') }, { v: 'b', l: t('Comparaison') }, { v: 'd', l: t('Écart|entre deux périodes') },
  ];
  return (
    <>
      <span style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-500)' }}>{t('Afficher')}</span>
      <span role="group" aria-label={t('Afficher')} style={{
        display: 'inline-flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)',
      }}>
        {items.map((x) => {
          const on = x.v === value;
          return (
            <button key={x.v} type="button" aria-pressed={on} onClick={() => onChange(x.v)}
              style={{
                height: 28, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--paper)' : 'transparent',
                color: on ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: on ? '0 1px 4px rgba(12,14,16,.14)' : 'none',
                fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
              }}>
              {x.l}
            </button>
          );
        })}
      </span>
    </>
  );
}

// ─── Le tri ──────────────────────────────────────────────────────────────────────────────────────

type Row = SalesReportView['rows'][number];

/** Une ligne telle qu'une cellule la lit : ses ventes, et ses blocs (`null` = rien à lire, ou bloc non demandé). */
interface CellSource { sales: SalesReportValues; stock: SalesStockValues | null; orders: SalesOrderValues | null }

/** La valeur d'une ligne pour une colonne et un affichage — `null` quand elle ne se mesure pas. */
function cellValue(v: CellSource, k: ReportMetric, display: ReportDisplay): number | null {
  if (isOrderMetric(k)) {
    if (!v.orders) return null;
    if (display === 'd') return v.orders.growth?.[k] ?? null;
    const src = display === 'b' ? v.orders.b : v.orders.a;
    return src ? src[k] : null;
  }
  if (isStockMetric(k)) {
    if (!v.stock) return null;
    if (display === 'd') return v.stock.growth?.[k] ?? null;
    const src = display === 'b' ? v.stock.b : v.stock.a;
    return src ? src[k] : null;
  }
  if (display === 'd') return v.sales.growth?.[k] ?? null;
  const src = display === 'b' ? v.sales.b : v.sales.a;
  return src ? src[k] : null;
}
const rowSource = (r: Row): CellSource => ({ sales: r, stock: r.stock, orders: r.orders });
const totalSource = (view: SalesReportView): CellSource => ({ sales: view.total, stock: view.stockTotal, orders: view.ordersTotal });

/** Les lignes dans l'ordre demandé — à égalité de niveaux, le plus gros CA net d'abord ; un vendeur se range par son nom. */
export function sortReportRows(rows: Row[], axes: readonly SalesReportLevel[], sort: ReportSort, display: ReportDisplay, lang: string): Row[] {
  return sortStatsRows(rows, axes.length, sort, (r, k) => cellValue(rowSource(r), k as ReportMetric, display), (x, y) => y.a.netTtcCents - x.a.netTtcCents, lang,
    (p, i) => (axes[i] === 'seller' ? p.label : p.value));
}

// ─── Les cellules ────────────────────────────────────────────────────────────────────────────────

/** Une valeur mise en forme selon son unité. */
function formatValue(v: number | null, unit: Unit): string {
  if (unit === 'money') return eur0(v);
  if (unit === 'rate') return pc(v);
  if (unit === 'coef') return coef(v);
  return qty(v);
}

/** Le texte, la teinte et la graisse d'une cellule — les seuils de la maquette. */
function cell(v: CellSource, c: ReportColumn, display: ReportDisplay): GridCell {
  const value = cellValue(v, c.k, display);
  if (display === 'd') {
    if (value === null) return { text: '—', fg: 'var(--ink-300)', fw: 600 };
    const flat = Math.abs(value) < 50;
    return { text: dTx(value), fg: flat ? 'var(--ink-500)' : value > 0 ? 'var(--teal-800)' : 'var(--rose-fg)', fw: 700 };
  }
  const fg = c.k === 'marginRateBp' && value !== null && value < 2500 ? 'var(--rose-fg)'
    : c.k === 'discountRateBp' && value !== null && value > 1200 ? 'var(--amber-fg)'
      : c.block === 'share' ? 'var(--ink-600)'
        : value === null ? 'var(--ink-300)' : 'var(--ink-800)';
  return { text: formatValue(value, c.unit), fg, fw: c.k === 'netTtcCents' || c.k === 'marginHtCents' ? 700 : 600 };
}

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

/**
 * LE TABLEAU DU REPORTING VENTES — ses colonnes et ses cellules, dans le squelette commun des reportings.
 * `filtered` dit si un filtre est posé : l'état vide ne dit pas la même chose avec ou sans.
 */
export function StatsReportTable({ view, visible, display, sort, onSort, filtered, onReset, lang, busy = false, label }: {
  view: SalesReportView;
  visible: ReportColumn[];
  display: ReportDisplay;
  sort: ReportSort;
  onSort: (next: ReportSort) => void;
  filtered: boolean;
  onReset: () => void;
  lang: string;
  busy?: boolean;
  /** Le nom du tableau pour les technologies d'assistance — celui de l'onglet. */
  label?: string;
}) {
  const axes = view.axes;
  const shownDisplay: ReportDisplay = view.reference ? display : 'a';
  const rows = sortReportRows(view.rows, axes, sort, shownDisplay, lang);
  const totalSub = `${shownDisplay === 'd' ? `${t('écart A/B')} · ` : shownDisplay === 'b' ? `${t('période B')} · ` : ''}${
    t('{n} ligne', { n: view.rows.length })}${sort.man ? '' : ` · ${t('ordre de la ventilation')}`}`;

  return (
    <StatsGrid label={label ?? t('Reporting ventes')} sort={sort} onSort={onSort} busy={busy}
      axes={axes.map((k) => ({ label: levelLabel(k), code: k === 'art' }))}
      columns={visible.map((c) => ({ k: c.k, label: columnLabel(c), width: c.width, color: BLOCKS[c.block].color }))}
      total={{ sub: totalSub, cells: visible.map((c) => cell(totalSource(view), c, shownDisplay)) }}
      rows={rows.map((r) => ({
        key: r.parts.map((p) => p.value).join('\u001f'), parts: shownParts(r.parts, axes), sub: t('{n} article', { n: r.articles }),
        cells: visible.map((c) => cell(rowSource(r), c, shownDisplay)),
      }))}
      after={(
        <>
          {view.rows.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center' }}>
              {filtered ? (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun article ne correspond à ces filtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>
                    <Trans k='Les filtres se cumulent en ET — <1>effacez-les</1> pour repartir.' parts={[(x) => (
                      <button type="button" onClick={onReset} style={{
                        border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}>{x}</button>
                    )]} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucune vente sur cette période')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Choisissez une autre période, ou comparez-la à une autre.')}</div>
                </>
              )}
            </div>
          )}
          <div style={{
            padding: '11px 14px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', borderRadius: '0 0 var(--r-sm) var(--r-sm)',
            fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55,
          }}>
            <Trans k='<1>Les taux se recalculent sur les sommes</1>, jamais en moyennant des pourcentages : le taux de remise du total est <2>1 − CA net / CA brut</2>, la marge <3>CA net HT − {achat} HT</3>, et le coef <4>CA net TTC / {achat} HT</4>. Un indicateur non mesurable affiche « — », jamais zéro.'
              vars={{ achat: fieldLabel('purchasePriceExclTax') }}
              parts={[
                (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>,
                (x) => <span style={{ fontFamily: 'var(--font-mono)' }}>{x}</span>,
                (x) => <span style={{ fontFamily: 'var(--font-mono)' }}>{x}</span>,
                (x) => <span style={{ fontFamily: 'var(--font-mono)' }}>{x}</span>,
              ]} />
            {needsStock(visible) && (
              <>
                {' '}
                {t('Le stock est celui de la fin de la période, valorisé au PMP de la date — le même que le Reporting stock ; entrées et sorties comptent tous les mouvements du grand livre, et le taux de sortie est vendu ÷ (vendu + stock).')}
              </>
            )}
            {needsOrders(visible) && (
              <>
                {' '}
                {t('Les commandes sont celles passées pendant la période, au prix d’achat HT net de leur remise, port exclu ; le reste à livrer est celui d’aujourd’hui — une commande clôturée n’attend plus rien.')}
              </>
            )}
          </div>
        </>
      )} />
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les niveaux, les colonnes affichées dans leur ordre, le tri, la ligne de total.
 * ⚠️ UN MONTANT INCONNU RESTE VIDE dans le fichier — un 0 dans un tableur devient une somme fausse.
 * ⚠️ LE FICHIER PORTE DES NOMBRES, PAS DU TEXTE MIS EN FORME : montants dans l'unité de la devise, taux en pourcentage.
 */
export function exportReport(
  view: SalesReportView, visible: ReportColumn[], display: ReportDisplay, sort: ReportSort, lang: string,
  /** Le début du nom du fichier — celui de l'onglet. */
  base = 'reporting-ventes',
): void {
  const axes = view.axes;
  const shownDisplay: ReportDisplay = view.reference ? display : 'a';
  const value = (v: CellSource, c: ReportColumn): string => {
    const x = cellValue(v, c.k, shownDisplay);
    if (x === null) return '';
    if (shownDisplay === 'd' || c.unit === 'rate') return csvRate(x);
    if (c.unit === 'money') return toInputValue(x);
    if (c.unit === 'coef') return csvCoef(x);
    return csvQty(x);
  };
  const unitOf = (c: ReportColumn) => (shownDisplay === 'd' || c.unit === 'rate' ? `${columnLabel(c)} (%)` : c.unit === 'money' ? `${columnLabel(c)} (${currentCurrency().code})` : columnLabel(c));
  // ⚠️ LE FICHIER DIT CE QU'IL CONTIENT : un export de la comparaison ou de l'écart, nommé et intitulé comme celui de la
  //    période, en serait indiscernable une fois ouvert — et des chiffres de l'an dernier passeraient pour ceux du mois.
  const head = (c: ReportColumn) => (shownDisplay === 'b' ? t('{v} — période B', { v: unitOf(c) })
    : shownDisplay === 'd' ? t('{v} — écart A/B', { v: unitOf(c) }) : unitOf(c));
  const ref = view.reference;
  const fileName = shownDisplay === 'b' && ref ? `${base}-comparaison-${ref.from}-${ref.to}`
    : shownDisplay === 'd' && ref ? `${base}-ecart-${view.period.from}-${view.period.to}-vs-${ref.from}-${ref.to}`
      : `${base}-${view.period.from}-${view.period.to}`;
  const rows = sortReportRows(view.rows, axes, sort, shownDisplay, lang);
  exporterCsv(fileName,
    [...axes.map(levelLabel), t('Articles'), ...visible.map(head)],
    [
      [t('TOTAL'), ...axes.slice(1).map(() => ''), String(view.scope.references), ...visible.map((c) => value(totalSource(view), c))],
      ...rows.map((r) => [...shownParts(r.parts, axes).map((p) => (p.label !== p.value ? `${p.value} — ${shownValue(p.label)}` : shownValue(p.value))), String(r.articles), ...visible.map((c) => value(rowSource(r), c))]),
    ]);
}
